"use client";

import React from "react";
import { ArrowRight, ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceProductsProvider, useEcommerceProducts, ProductSortDropdown, ProductsFilter, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
declare const wvcClient: {
  getCurrentWPQuery?: () => Record<string, unknown>;
  getCurrentWPQueryParams?: () => Record<string, unknown>;
};
function ProductsCatalogConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    total
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((_, i) => <div key={i} data-index={i} className="border border-border bg-card p-0 flex flex-col">
            <div className="aspect-[16/10] w-full bg-muted/60 animate-pulse" />
            <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
              <div className="space-y-2">
                <div className="h-4 w-24 bg-muted animate-pulse" />
                <div className="h-6 w-3/4 bg-muted animate-pulse" />
              </div>
              <div className="h-10 w-full bg-muted/40 animate-pulse mt-4" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-destructive/40 bg-muted p-8 text-center">
        <p className="font-mono text-sm text-destructive">
          System notice: Unable to query catalog telemetry.
        </p>
        <Button onClick={() => refetch()} variant="outline" size="sm" className="mt-4 font-mono text-xs uppercase">
          Retry Connection
        </Button>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="border border-border bg-muted/40 p-12 text-center">
        <p className="font-serif text-xl font-bold uppercase tracking-wider text-foreground">
          No equipment units match the selected parameters.
        </p>
        <p className="mt-2 text-sm text-muted-foreground font-sans">
          Clear or modify sidebar criteria to view available machinery and components.
        </p>
      </div>;
  }
  return <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
        {products.map((product, i) => {
        const isUsed = product.categories?.some(cat => cat.name.toLowerCase().includes("used")) || product.title.toLowerCase().includes("used");
        const isPart = product.categories?.some(cat => {
          const catName = cat.name.toLowerCase();
          return catName.includes("part") || catName.includes("accessor");
        }) || product.title.toLowerCase().includes("part") || product.title.toLowerCase().includes("accessor");
        const label = isPart ? "Enquire About This Product" : "Enquire About This Bull";
        return <article key={product.id} data-index={i} className="group flex flex-col border border-border bg-card transition-colors duration-150 hover:border-foreground">
              {/* Product Visual & Status Plate */}
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted p-4">
                {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" className="h-full w-full object-contain object-center grayscale-[15%] contrast-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground font-mono text-xs uppercase">
                    Spec Plate Visual Pending
                  </div>}
                <div className="absolute top-3 left-3">
                  <span className={`inline-block px-2.5 py-1 font-mono text-xs font-medium uppercase tracking-wider ${isUsed ? "border border-border bg-muted text-foreground" : "bg-foreground text-background"}`}>
                    {isUsed ? "Verified Used" : "New Unit"}
                  </span>
                </div>
              </div>

              {/* Machine Technical Detail Block */}
              <div className="flex flex-1 flex-col justify-between p-6">
                <div>
                  <div className="flex items-center justify-between gap-2 border-b border-border pb-2">
                    <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                      ID: #{product.id}
                    </span>
                    {product.categories?.[0] && <span className="font-mono text-xs text-muted-foreground uppercase">
                        {product.categories[0].name}
                      </span>}
                  </div>

                  <Link to={product.link} className="block mt-3 group-hover:text-primary transition-colors">
                    <h2 className="font-serif text-2xl font-bold uppercase tracking-wide text-foreground leading-tight">
                      {product.title}
                    </h2>
                  </Link>

                  {product.short_description ? <div className="mt-3 font-sans text-sm text-muted-foreground line-clamp-2 leading-relaxed" dangerouslySetInnerHTML={{
                __html: product.short_description
              }} /> : <p className="mt-3 font-sans text-sm text-muted-foreground leading-relaxed">
                      Commercial heavy-duty assembly engineered for high-duty cycle operations and venue deployment.
                    </p>}
                </div>

                <div className="mt-6 pt-4 border-t border-border">
                  <div className="flex items-baseline justify-between mb-4">
                    <span className="font-mono text-xs text-muted-foreground uppercase">
                      Commercial Price
                    </span>
                    <ProductPrice product={product} className="font-serif text-2xl font-bold tracking-tight text-primary" />
                  </div>

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Button asChild className="w-full bg-primary text-foreground-primary rounded-none font-serif uppercase tracking-wider text-sm font-semibold h-10 hover:bg-primary/90 transition-colors">
                      <Link to="/RequestQuotePage">
                        {label}
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background font-serif uppercase tracking-wider text-sm font-semibold h-10 transition-colors">
                      <Link to={product.link}>
                        Specs <ArrowRight className="ml-1 size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </article>;
      })}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6 mt-10">
          <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            Displaying Page {currentPage} of {totalPages} (Total {total} units)
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="rounded-none border-border font-mono text-xs uppercase disabled:opacity-40">
              <ChevronLeft className="size-3.5 mr-1" />
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="rounded-none border-border font-mono text-xs uppercase disabled:opacity-40">
              Next
              <ChevronRight className="size-3.5 ml-1" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function Listings() {
  const wpQuery = React.useMemo(() => {
    if (typeof wvcClient !== "undefined" && typeof wvcClient.getCurrentWPQuery === "function") {
      const query = wvcClient.getCurrentWPQuery();
      if (query) return query;
    }
    if (typeof wvcClient !== "undefined" && typeof wvcClient.getCurrentWPQueryParams === "function") {
      const params = wvcClient.getCurrentWPQueryParams();
      if (params) return new WP_Query(params);
    }
    return new WP_Query({
      post_type: "product",
      posts_per_page: 9
    });
  }, []);
  return <section id="product-listings" data-nav="light" className="bg-background text-foreground py-12 md:py-16 border-b border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <EcommerceProductsProvider className="w-full" wp_query={wpQuery}>
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
            {/* Dedicated Industrial Specifications Sidebar Filter */}
            <aside className="hidden w-full lg:block lg:w-1/4 shrink-0">
              <div className="sticky top-24 border border-border bg-card p-6">
                <div className="flex items-center gap-2 border-b border-border pb-4 mb-6">
                  <SlidersHorizontal className="size-4 text-primary" />
                  <span className="font-serif text-lg font-bold uppercase tracking-wider text-foreground">
                    Equipment Filters
                  </span>
                </div>
                <ProductsFilter />
              </div>
            </aside>

            {/* Main Machinery Inventory Column */}
            <div className="w-full lg:w-3/4 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4 border border-border bg-muted/40 p-4">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-primary inline-block" />
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground font-semibold">
                    Live Commercial Inventory
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <ProductSortDropdown label="Sort Catalog:" />
                </div>
              </div>

              <ProductsCatalogConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}